package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The strip that appears when rows are selected and says what can be done to them.
 *
 * **It animates on the crossing of zero and on nothing else.** `rememberLPresence` drives one
 * entrance when the selection becomes non-empty and one exit when it empties again; ticking a
 * fifth row changes a number in place. Motion that replayed on every tick would be a strobe over
 * a table the reader is working down, which is exactly the case the motion rules forbid.
 *
 * `opacity` and `transform` only, per the property rule — a strip that animated its height would
 * reflow the table above it on every frame of its own entrance, and the row under the pointer
 * would move out from under it.
 *
 * The bar is hairline on `surfaceRaised`, not elevated: `Elevation.Floating` is reserved for the
 * four surfaces that genuinely float over the page (menu, modal, toast, tooltip), and a strip
 * docked to the bottom of a table is part of the table's own frame.
 */
val LBulkActionBarStyle = CssStyle {
    base {
        Modifier
            // Sticky, not fixed. The bar belongs to one table, and a fixed strip follows the reader
            // onto every other section of a long page, still offering to delete rows they can no
            // longer see.
            .position(Position.Sticky)
            .bottom(Space.LG)
            .width(100.percent)
            .minHeight(HIT_AREA_FLOOR_PX.px)
            .zIndex(700)
            .backgroundColor(colorMode.toSitePalette().surfaceRaised)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Card)
            .padding(topBottom = Space.SM, leftRight = Space.MD)
            .gap(Space.MD)
            .styleModifier {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("align-items", "center")
                property(
                    "transition",
                    "opacity ${Motion.FastMs}ms ${Motion.EaseOut}, " +
                        "transform ${Motion.FastMs}ms ${Motion.EaseOut}",
                )
            }
    }
    cssRule("[data-presence=\"entering\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateY(${Motion.PopoverTravelPx}px)")
        }
    }
    cssRule("[data-presence=\"exiting\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateY(${Motion.PopoverTravelPx}px)")
            property("pointer-events", "none")
        }
    }
}

val LBulkActionBarCountStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textPrimary)
        .fontSize(TypeScale.BodySmall)
        .fontWeight(600)
        .tabularNumbers()
}

val LBulkActionBarNoteStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
}

/**
 * What the bar keeps saying while it is on its way out.
 *
 * Plain fields behind a `remember`, deliberately not snapshot state: this is read and written in
 * the same composition, and a `MutableState` written during composition either invalidates the
 * scope that just read it or has to be deferred into an effect — which would leave the live region
 * a frame behind the count it is announcing.
 */
private class LBulkActionBarRetained(var label: String, var count: Int)

/**
 * @param selectedCount how many rows are selected. Zero dismisses the bar.
 * @param label the sentence naming the selection — "3 keys selected". It is both the bar's
 *   accessible name and the text of its live region, so it carries the count in words the reader's
 *   language actually uses; build it with `tPlural`, not with a bare number and an "s".
 * @param note the honesty line, when there is one to draw: "Selection covers the 200 loaded rows".
 * @param returnFocusToId the id of the element to put focus back inside when the reader clears the
 *   selection — the select-all header cell. Without it, clearing removes the focused button and
 *   strands a keyboard reader on `<body>` in the middle of a table.
 */
@Composable
fun LBulkActionBar(
    selectedCount: Int,
    onClear: () -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    note: String? = null,
    clearLabel: String = t("ds.ds.lbulk_action_bar.clear_selection", "Clear selection"),
    returnFocusToId: String? = null,
    actions: @Composable RowScope.() -> Unit,
) {
    // `rememberLPresence`, not `LPresenceState.initial`: the latter reports PRESENT from the first
    // frame and HIDDEN the moment the count reaches zero, so the strip would cut in and cut out
    // between two frames — the jump cut under a table the reader is working down, on the one strip
    // that carries destructive actions.
    val presence = rememberLPresence(selectedCount > 0, Motion.FastMs)
    val retained = remember { LBulkActionBarRetained(label, selectedCount) }
    if (selectedCount > 0) {
        retained.label = label
        retained.count = selectedCount
    }
    // The label the caller passes at zero says "0 selected", and the bar is still on screen for the
    // length of its exit. Holding the last real one means the strip fades out saying what it said,
    // rather than counting itself down to nothing on the way.
    val shownLabel = if (selectedCount > 0) label else retained.label
    val shownCount = if (selectedCount > 0) selectedCount else retained.count

    if (!presence.rendered) return
    val exiting = presence.phase == LPresencePhase.EXITING

    Section(
        LBulkActionBarStyle.toModifier()
            .then(modifier)
            .toAttrs {
                attr("aria-label", shownLabel)
                attr("data-bulk-action-bar", "")
                attr("data-presence", presence.phase.attributeValue)
                if (exiting) {
                    attr("inert", "")
                    attr("aria-hidden", "true")
                }
            },
    ) {
        // Deliberately *not* wrapped in `key(selectedCount)`. Keying here would give the live
        // region a new identity on every tick, and Compose would replace the node rather than
        // rewrite its text — which assistive technology reports as a whole new region and reads
        // from the top each time.
        Span(
            LBulkActionBarCountStyle.toModifier().toAttrs {
                attr("data-bulk-count", shownCount.toString())
                // Polite, and on a node that outlives every count change inside one selection, so
                // the reader hears "4 keys selected" rather than the whole bar being announced
                // again from the top on every tick.
                attr("aria-live", "polite")
                attr("aria-atomic", "true")
            },
        ) {
            Text(shownLabel)
        }
        if (note != null) {
            Span(LBulkActionBarNoteStyle.toModifier().toAttrs { attr("data-bulk-note", "") }) {
                Text(note)
            }
        }
        Row(
            Modifier
                .gap(Space.SM)
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("margin-inline-start", "auto")
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            actions()
            LButton(
                label = clearLabel,
                onClick = {
                    onClear()
                    // The button that clears is inside the strip that clearing removes. Without
                    // this, pressing it drops focus onto `<body>` and a keyboard reader's next Tab
                    // starts again at the top of the document, above a table of two hundred rows.
                    returnFocusToId?.let(::focusFirstControlIn)
                },
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
            )
        }
    }
}
